import { icons } from "assets/icons";
import { Tooltip } from "components/Tooltip";
import { Horizontal } from "gls/lib";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { style } from "typestyle";
import { useUser } from "../hooks/useUser";
import { useDataTableStore, useUserManagementStore, useUserRolePermissionStore } from "contexts/RootStore.context";
import IconButton from "components/Button/IconButton";
import { colours } from "styling/colours";
import { GenericModal } from "components/Modals/GenericModal";
import { UserDto } from "../UserDto";

type OperationsCellProps = {
  rowIndex: number,
  rowValues: Record<string, any>,
}

type changePassModal = {
  show: boolean,
  rowValues: Record<string, any> | null,
}

const OperationsCell = observer(({ rowIndex, rowValues }: OperationsCellProps) => {

  const [changePassModal, setChangePassModal] = useState<changePassModal>({
    show: false,
    rowValues: null
  });
  const { isAnyPendingImportProcess } = useUserManagementStore();
  const { canManageUsers } = useUserRolePermissionStore();
  const { editingRowIndex,
    stopEditingRow,
    isValidRow,
    isEditingRowChanged,
    setScrollingButtonShow,
  } = useDataTableStore();

  const {
    EditRowHandler,
    saveHandler,
    ResetUserPasswordHandler,
  } = useUser();

  return (
    <>
      <Tooltip
        contentClassName={style({ height: "100%" })}
        content={
          <Horizontal className={style({ height: "100%" })} spacing={0} padding={8}>
            {editingRowIndex !== rowIndex &&
              <>
                {canManageUsers &&
                  <IconButton
                    ariaLabel="Edit Row"
                    id='button-edit'
                    disabled={isAnyPendingImportProcess}
                    fill={isAnyPendingImportProcess ? colours.disableIcon : colours.secondary}
                    icon={
                      <icons.EditIconOutlined
                        aria-label="Edit Row"
                        width={18}
                        height={18}
                      />
                    }
                    onClick={() => EditRowHandler(rowIndex)}
                  />
                }
                {canManageUsers && !rowValues.federated && rowValues.is_resend_welcome_email_available &&
                  <IconButton
                    ariaLabel="Reset Password"
                    disabled={isAnyPendingImportProcess}
                    id='button-reset-password'
                    fill={isAnyPendingImportProcess ? colours.disableIcon : colours.secondary}
                    icon={
                      <icons.PasswordResetIcon
                        aria-label="Reset Password"
                        width={18}
                        height={18}
                      />
                    }
                    onClick={() => {
                      setChangePassModal({
                        show: true,
                        rowValues: rowValues
                      })
                    }}
                  />
                }
              </>
            }
            {editingRowIndex === rowIndex &&
              <>
                <IconButton
                  ariaLabel="Save Row"
                  id='button-edit'
                  fill={isValidRow ? colours.success : colours.tertiaryText}
                  icon={
                    <icons.SuccessIcon
                      aria-label="Save Row"
                      width={18}
                      height={18}
                    />
                  }
                  disabled={!(isValidRow && isEditingRowChanged)}
                  onClick={() => {
                    saveHandler();
                    setScrollingButtonShow(true);
                  }}
                />
                <IconButton
                  ariaLabel="Cancel Edit"
                  id='button-cancel'
                  fill={colours.error}
                  icon={
                    <icons.CrossIcon
                      aria-label="Cancel Edit"
                      width={18}
                      height={18}
                    />
                  }
                  onClick={() => {
                    stopEditingRow();
                    setScrollingButtonShow(true);
                  }}
                />
              </>
            }
          </Horizontal>
        } >
        {isAnyPendingImportProcess ? "Import in progress. Please try again later." : ""}
      </Tooltip>

      <GenericModal
        title="Resend Welcome Email"
        subtitle={<>Are you sure you want to resend welcome email to <b>{changePassModal.rowValues?.name}</b>?</>}
        actionName="Resend"
        cancelName="Cancel"
        icon={<icons.PasswordResetIcon aria-label="Reset Password" height={18} width={18} fill={colours.secondaryText} />}
        isOpen={changePassModal.show}
        onClose={() => {
          setChangePassModal({
            show: false,
            rowValues: null
          });
        }}
        onAction={() => {
          if (changePassModal.rowValues) {
            ResetUserPasswordHandler(changePassModal.rowValues as UserDto);
            setChangePassModal({
              show: false,
              rowValues: null
            });
          }
        }}
      />
    </>
  )
});

export default OperationsCell;